import moment from "moment/moment";

import { CropsWithMissingHistoricalPracticeDataByYear, CropsWithMissingHistoricalPracticeDataItem, PRACTICE_SLUG, PracticeFCPO, ZERO_UUID } from "models";

import { getCropYearFromDates } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import {
  getFirstUnlockedYear,
  getHasSubmissionForSoilPlus3StartYear,
  getIsSoilPlus3DataCollectionEditable,
} from "pages/app/all-field-view/utils/dataEntryUtils.ts";

// mineral fertilizer only reaches here from the all-fields data table view, so its practices are optional
type EditableStateItem = Omit<CropsWithMissingHistoricalPracticeDataItem, "assignedPractices"> & {
  assignedPractices: CropsWithMissingHistoricalPracticeDataItem["assignedPractices"] & {
    [PRACTICE_SLUG.MINERAL_FERTILIZERS]?: PracticeFCPO[] | null;
  };
};

// TODO (Alp): Write tests
export const getEditableState = (
  item: EditableStateItem,
  activeFarmReportingYear: number | undefined,
  isReportingYearLocked: boolean,
  // overrides the derived cropYear with the row's own year bucket, so Main/Cover/Undersowing shown together lock consistently
  rowYear: number | undefined,
  isAdminOrImpersonator: boolean,
  lastSubmissionYear: number | null | undefined
): {
  isGrowingPeriodEditable: boolean;
  isTillageEditable: boolean;
  isSeedingEditable: boolean;
  isTillageDepthEditable: boolean;
  areTillageDatesEditable: boolean;
  isOrganicEditable: boolean;
  areOrganicDatesEditable: boolean;
  isMineralEditable: boolean;
  areMineralDatesEditable: boolean;
} => {
  const cropYear =
    rowYear ??
    getCropYearFromDates(
      item.crop.cropType,
      moment(item.crop.seedingStartDate ?? item.crop.fallbackDates.seedingStartDate),
      moment(item.crop.harvestEndDate ?? item.crop.fallbackDates.harvestEndDate)
    );
  const isCurrentOrFutureYear = !!activeFarmReportingYear && !!cropYear && cropYear >= getFirstUnlockedYear(activeFarmReportingYear, isReportingYearLocked);
  if (isCurrentOrFutureYear) {
    return {
      isGrowingPeriodEditable: true,
      isSeedingEditable: true,
      isTillageEditable: true,
      isTillageDepthEditable: true,
      areTillageDatesEditable: true,
      isOrganicEditable: true,
      areOrganicDatesEditable: true,
      isMineralEditable: true,
      areMineralDatesEditable: true,
    };
  }

  const hasSubmissionForSoilPlus3StartYear = getHasSubmissionForSoilPlus3StartYear(lastSubmissionYear);

  let isGrowingPeriodEditable = false;
  let isSeedingEditable = false;
  let isTillageEditable = false;
  let isTillageDepthEditable = false;
  let areTillageDatesEditable = false;
  let isOrganicEditable = false;
  let areOrganicDatesEditable = false;
  let isMineralEditable = false;
  let areMineralDatesEditable = false;

  // SoilPlus 3.0 data collection only stays editable until the farm submits, so wherever "!hasSubmissionForSoilPlus3StartYear"
  // already unlocks a value, its soilPlus3 flag needs no separate check
  // Growing Period
  const hasCompleteGrowingPeriod = item.crop.seedingStartDate !== null && item.crop.harvestEndDate !== null;
  if (!hasCompleteGrowingPeriod || isAdminOrImpersonator || !hasSubmissionForSoilPlus3StartYear) {
    isGrowingPeriodEditable = true;
  }

  // Seeding
  const seedingExist = item.assignedPractices.seedings?.[0];
  const seedingIsPartOfSoilPlus3DataCollection = getIsSoilPlus3DataCollectionEditable(item.crop.seedingSoilPlus3DataCollection, lastSubmissionYear);
  if (!seedingExist) {
    isSeedingEditable = true;
  } else {
    isSeedingEditable = isAdminOrImpersonator || seedingIsPartOfSoilPlus3DataCollection;
  }

  // Tillage
  const existingTillages = item.assignedPractices.tillages;
  if (!existingTillages) {
    isTillageEditable = true;
    isTillageDepthEditable = true;
    areTillageDatesEditable = true;
  } else if (existingTillages.length === 0) {
    isTillageEditable = true;
    isTillageDepthEditable = true;
    areTillageDatesEditable = true;
  } else {
    const allSameSoilPlus3DataCollection = existingTillages.every((tillage, _, array) => tillage.soilPlus3DataCollection === array[0].soilPlus3DataCollection);
    const someConfirmed = existingTillages.some((tillage) => tillage.isConfirmed);
    const isSoilPlusEditable = allSameSoilPlus3DataCollection
      ? getIsSoilPlus3DataCollectionEditable(existingTillages[0].soilPlus3DataCollection, lastSubmissionYear)
      : false;

    if (isSoilPlusEditable) {
      isTillageEditable = true;
    } else {
      isTillageEditable = isAdminOrImpersonator || !someConfirmed;
    }

    isTillageDepthEditable = isAdminOrImpersonator || !hasSubmissionForSoilPlus3StartYear || !someConfirmed;

    // Tillage dates
    if (existingTillages.some((tillage) => tillage.endDate === null && tillage.assignedPracticeId !== ZERO_UUID)) {
      areTillageDatesEditable = true;
    } else {
      areTillageDatesEditable = isAdminOrImpersonator || !hasSubmissionForSoilPlus3StartYear;
    }
  }

  // Organic Fertilizer
  const existingOrganicFertilizers = item.assignedPractices.organicFertilizers;
  if (!existingOrganicFertilizers) {
    isOrganicEditable = true;
    areOrganicDatesEditable = true;
  } else if (existingOrganicFertilizers.length === 0) {
    isOrganicEditable = true;
    areOrganicDatesEditable = true;
  } else {
    const allSameSoilPlus3DataCollection = existingOrganicFertilizers.every(
      (organicFertilizer, _, array) => organicFertilizer.soilPlus3DataCollection === array[0].soilPlus3DataCollection
    );
    const someConfirmed = existingOrganicFertilizers.some((organicFertilizer) => organicFertilizer.isConfirmed);
    const isSoilPlusEditable = allSameSoilPlus3DataCollection
      ? getIsSoilPlus3DataCollectionEditable(existingOrganicFertilizers[0].soilPlus3DataCollection, lastSubmissionYear)
      : false;

    const hasMissingEndDate = existingOrganicFertilizers.some(
      (organicFertilizer) => organicFertilizer.endDate === null && organicFertilizer.assignedPracticeId !== ZERO_UUID
    );
    if (isSoilPlusEditable || hasMissingEndDate) {
      isOrganicEditable = true;
    } else {
      isOrganicEditable = isAdminOrImpersonator || !someConfirmed;
    }

    // Organic fertilizer dates
    if (existingOrganicFertilizers.some((organicFertilizer) => organicFertilizer.endDate === null && organicFertilizer.assignedPracticeId !== ZERO_UUID)) {
      areOrganicDatesEditable = true;
    } else {
      areOrganicDatesEditable = isAdminOrImpersonator || !hasSubmissionForSoilPlus3StartYear;
    }
  }

  const existingMineralFertilizers = item.assignedPractices[PRACTICE_SLUG.MINERAL_FERTILIZERS];
  if (!existingMineralFertilizers || existingMineralFertilizers.length === 0) {
    isMineralEditable = true;
    areMineralDatesEditable = true;
  } else {
    const someConfirmed = existingMineralFertilizers.some((mineralFertilizer) => mineralFertilizer.isConfirmed);
    const hasMissingEndDate = existingMineralFertilizers.some(
      (mineralFertilizer) => mineralFertilizer.endDate === null && mineralFertilizer.assignedPracticeId !== ZERO_UUID
    );
    isMineralEditable = hasMissingEndDate || isAdminOrImpersonator || !someConfirmed;

    // Mineral fertilizer dates follow the same rule as the organic ones, which is what the single-crop form applies too
    areMineralDatesEditable = hasMissingEndDate || isAdminOrImpersonator || !hasSubmissionForSoilPlus3StartYear;
  }

  return {
    isGrowingPeriodEditable,
    isSeedingEditable,
    isTillageEditable,
    isTillageDepthEditable,
    areTillageDatesEditable,
    isOrganicEditable,
    areOrganicDatesEditable,
    isMineralEditable,
    areMineralDatesEditable,
  };
};

export const isYearComplete = (year: number, cropsWithMissingHistoricalPracticeDataByYear: CropsWithMissingHistoricalPracticeDataByYear) => {
  const yearEntries = cropsWithMissingHistoricalPracticeDataByYear[year];
  return (
    yearEntries?.length === 0 ||
    yearEntries?.every((entry) => {
      const hasCompleteGrowingPeriod = entry.crop.seedingStartDate !== null && entry.crop.harvestEndDate !== null;
      if (!hasCompleteGrowingPeriod) return false;
      const { isTillageDepthRequired } = entry;
      const { seedings, tillages, organicFertilizers } = entry.assignedPractices;
      const hasMissingTillageDepth =
        isTillageDepthRequired &&
        tillages !== null &&
        tillages.length > 0 &&
        tillages.some((practice) => practice.tillageDepth === null && practice.assignedPracticeId !== ZERO_UUID);
      if (hasMissingTillageDepth) return false;
      const hasMissingTillageEndDate =
        tillages !== null && tillages.length > 0 && tillages.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
      if (hasMissingTillageEndDate) return false;
      const hasMissingOrganicFertilizerEndDate =
        organicFertilizers !== null &&
        organicFertilizers.length > 0 &&
        organicFertilizers.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
      if (hasMissingOrganicFertilizerEndDate) return false;
      return [seedings, tillages, organicFertilizers].every((practice) => practice === null || (Array.isArray(practice) && practice.length > 0));
    })
  );
};
